import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import { useInView } from 'react-intersection-observer';

import styles from './GameThreePart.module.less';
import ThreePart from '../../components/Game/ThreePart/ThreePart';
import { getSoftType, sortSoftsByLocalSoftInit } from '../../utils/utils';
import { CONTEXT } from '../../config';
import { logging, logPostInfo } from '../../actions/log';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectwidowSize = createSelector(
  state => state.layout,
  layout => layout.widowSize,
);

const selectLocalSoftsInit = createSelector(
  state => state.native,
  native => native.installedSoftInit,
);

export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);
  const localSoftsInit = useSelector(selectLocalSoftsInit);
  const windowSize = useSelector(selectwidowSize);

  const [aPageSize, setAPageSize] = useState(3); // 当前一页展示的应用数量
  const [aShowOnePage, setAShowOnePage] = useState({
    showStart: 0,
    showEnd: 3,
  }); // 当前展示那些应用的游标
  const [isInChange, setIsInChange] = useState(false); // 主要为了处理，翻页时，展现数据不上报的问题

  const [showData, setShowData] = useState(null); // 所有信息
  const [allSofts, setAllSofts] = useState([]); // 排序后可展示的应用

  const LOG_ACTION_SOURCE = {
    c_more: `${logInfo.clTag || logInfo.tag}_Group_More`,
    c_change: `${logInfo.clTag || logInfo.tag}_Group_ChangePage`,
    source: `${logInfo.tag}_Group`,
  };

  const [refInView, inView] = useInView({
    threshold: 0,
  });

  const [refInView75, inView75] = useInView({
    threshold: 0.75,
  });

  useEffect(() => {
    if (inView75 && logInfo && logInfo.changView) {
      logInfo.changView({
        ...logInfo,
        pageCardId: data && data.dataList && data.dataList[0] && data.dataList[0].pageCardContentId,
        pageCardTitle: data && data.title,
        rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
        cardType: data && data.cardType,
        org: (data && data.dataList && data.dataList[0] && data.dataList[0].org) || '',
      });
    }
  }, [inView75]);

  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    if (data && data.dataList && data.dataList.length > 0 && data.dataList[0].apps) {
      let allSoftsArr = sortSoftsByLocalSoftInit(data.dataList[0].apps || [], localSoftsInit);

      setAllSofts(allSoftsArr);
      setShowData(data.dataList[0]);
    }
  }, [dispatch, data, localSoftsInit]);

  // 处理拉伸
  useEffect(() => {
    if (allSofts?.length > 0 && windowSize?.width) {
      let pageSize = calculationPageSize();
      setAPageSize(size => {
        if (size !== pageSize) {
          return pageSize;
        }
        return size;
      });
      setAShowOnePage(onePage => {
        let showOnePage = {
          showStart: onePage.showStart,
          showEnd: onePage.showStart + pageSize,
        };
        if (showOnePage.showEnd > allSofts.length) {
          showOnePage = {
            showStart: allSofts.length - pageSize,
            showEnd: allSofts.length,
          };
        }

        if (showOnePage.showStart < 0) {
          showOnePage.showStart = 0;
        }
        if (showOnePage.showEnd > allSofts.length) {
          showOnePage.showEnd = allSofts.length;
        }
        return showOnePage;
      });
    }
  }, [allSofts, windowSize]);

  // 根据窗口 宽度 计算需要显示的应用个数
  const calculationPageSize = useCallback(() => {
    let windowWidth = document.body.clientWidth || 1060;
    windowWidth = windowWidth > 1960 ? 1960 : windowWidth < 1060 ? 1060 : windowWidth;
    // 计算一行的个数 256n + 16(n-1) = 总宽度，所以 n = (总宽度 + 16) / 272
    let diff = 16;
    const onePageSize = parseInt((windowWidth - 180 - 80 + diff) / (256 + diff));
    return onePageSize;
  }, [windowSize, allSofts]);

  // 处理翻页
  const handleChangeSoft = useCallback(
    point => {
      if (allSofts && allSofts.length === 0) {
        return [];
      }

      let showOnePage = { ...aShowOnePage };

      if (point === 'next') {
        // 处理 aShowOnePage
        showOnePage.showStart = aShowOnePage.showStart + aPageSize; // 下一页开始
        showOnePage.showEnd = aShowOnePage.showEnd + aPageSize; // 下一页结束
        if (showOnePage.showEnd > allSofts.length) {
          showOnePage.showStart = allSofts.length - aPageSize;
          showOnePage.showEnd = allSofts.length;
        }
      }
      if (point === 'prev') {
        // 处理 aShowOnePage
        showOnePage.showStart = aShowOnePage.showStart - aPageSize; // 上一页开始
        showOnePage.showEnd = aShowOnePage.showEnd - aPageSize; // 上一页结束
        if (showOnePage.showStart < 0) {
          showOnePage.showStart = 0;
          showOnePage.showEnd = aPageSize;
        }
      }
      // 处理异常
      if (showOnePage.showStart < 0) {
        showOnePage.showStart = 0;
      }
      if (showOnePage.showEnd > allSofts.length) {
        showOnePage.showEnd = allSofts.length;
      }

      setIsInChange(true);
      let index = setTimeout(() => {
        setAShowOnePage(showOnePage);
        setIsInChange(false);
        clearTimeout(index);
      }, 100);

      // 点击换一换, flag第一次切换不上报
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_change,
          label: '',
          param2: {
            ...logInfo,
            source: `${LOG_ACTION_SOURCE.source}||${showData.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            pageCardContentId: showData.pageCardContentId,
            nextOrPrev: point,
            title: showData.name,
            // pageNum: getPageNum(showOnePage) || 1,
            // pageSize: aPageSize,
            // ...aShowOnePage,
          },
        }),
      );
    },
    [dispatch, logInfoData, showData, windowSize, allSofts, aPageSize, aShowOnePage],
  );

  // const getPageNum = useCallback(showOnePage => {
  //   let page = 1;
  //   let pageSize = calculationPageSize();
  //   if (showOnePage?.showEnd) {
  //     page = parseInt(showOnePage?.showEnd / pageSize);
  //     if (showOnePage?.showEnd % pageSize > 0) {
  //       page = page + 1;
  //     }
  //   }
  //   return page;
  // }, []);

  return (
    <div
      className={`${styles.card} ${inView ? styles.card_cardIn : styles.card_cardOut}`}
      ref={refInView}
      data-item={data.cardType}
    >
      {inView && (
        <div className={`${styles.card_box}`} ref={refInView75}>
          <div className={styles.card_header}>
            <div className={styles.card_header_title}>{data.title}</div>
            <div className={styles.header_change}>
              <div
                className={`iconfont icon-game_p_l ${
                  aShowOnePage.showStart === 0 ? styles.header_change_dis : styles.header_change_
                }`}
                onClick={() => {
                  if (aShowOnePage.showStart !== 0) {
                    handleChangeSoft('prev');
                  }
                }}
              ></div>
              <div
                className={`iconfont icon-game_p_r ${
                  aShowOnePage.showEnd === allSofts.length
                    ? styles.header_change_dis
                    : styles.header_change_
                }`}
                onClick={() => {
                  if (aShowOnePage.showEnd !== allSofts.length) {
                    handleChangeSoft('next');
                  }
                }}
              ></div>
            </div>
          </div>
          <div className={styles.content_soft_line}>
            {!isInChange &&
              allSofts &&
              allSofts.length > 0 &&
              allSofts.slice(aShowOnePage.showStart, aShowOnePage.showEnd).map((item, j) => {
                return (
                  <ThreePart
                    soft={{ ...item, cardShowDesc: true }}
                    logInfo={{
                      ...logInfo,
                      source: `${LOG_ACTION_SOURCE.source}||${showData.pageCardContentId}`,
                      source_p: logInfoData.source_p,
                      source_pp: logInfoData.source_pp,
                      ///////////////////////////
                      softid: item?.androidType === '1' ? item?.packageName : item?.softID,
                      softType: getSoftType(item),
                      contentType: 'soft',
                      rankCode: showData.code,
                      org: showData.org || '',
                      cardType: data.cardType,
                      cardName: data.cardType,
                      pageCardTitle: data.title,
                      pageCardId: showData.pageCardContentId,
                      ///////////////////////////
                    }}
                    key={j}
                  />
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
});
